
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'

import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import ClickAwayListener from '@mui/material/ClickAwayListener';
import BoltIcon from '@mui/icons-material/Bolt';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';


import {
    isPrintDrawerOpenAtom, currentEntityNameForActionDrawerAtom, currentEntityIdForActionDrawerAtom,
    currentUserSessionAtom, hubConnectionIdAtom,
    colorsAtom,
    currentFunctionEntityParametersForActionDrawerAtom
} from 'library/store';
import { globalConfig } from 'config';


import entityService from 'features/services/Entity';
import BasicButtonList from './ui/BasicButtonList';
import { defaultFeatureDescription, IFeatureDescription, IFeatureParameter, IReportDescription } from 'library/interface';
import { isFalsy } from 'utility-types';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import { FormDialog } from './ui/FormDialog';
import { Box, Button, Chip, IconButton, MenuItem, Slide, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useMutation, useQuery } from 'react-query';
import ArrayField from './ui/ArrayField';

import { Watch, Triangle, RevolvingDot, Grid } from 'react-loader-spinner';
import { Enum_PRODUCTION_UNIT, IEnumerationItem } from 'features/configuration/models/Enumeration';
import useEnumerationService from 'features/configuration/services/Enumeration';

export const EntityPrintDrawer = () => {

    const { t, i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const { language: lg, userStores } = useRecoilValue(currentUserSessionAtom);

    const [Colors, setColors] = useRecoilState(colorsAtom);

    const [currentUserSession, setCurrentUserSession] = useRecoilState(currentUserSessionAtom);
    const [hubConnectionId, setHubConnectionId] = useRecoilState(hubConnectionIdAtom);

    const entityName = useRecoilValue(currentEntityNameForActionDrawerAtom);
    const entityId = useRecoilValue(currentEntityIdForActionDrawerAtom);
    const functionEntityParameters = useRecoilValue(currentFunctionEntityParametersForActionDrawerAtom);

    const [openActionParams, setOpenActionParams] = useState(false);
    const [isPrintDrawerOpen, setIsActionDrawerOpen] = useRecoilState(isPrintDrawerOpenAtom);

    const { getParametersByFeature, generateEntityReport, getReportsByEntityForUser, directPrint } = entityService();

    const { getEnumerationItemsByEnumerationCodes } = useEnumerationService();

    const [entityReports, setEntityReports] = useState<IReportDescription[]>([]);

    const methodsEntityAction = useForm<IReportDescription>({ defaultValues: defaultFeatureDescription });
    const { register, setValue, getValues, watch, reset, control, formState: { errors } } = methodsEntityAction;

    //const [isDirectReport, setIsDirectReport] = useState<boolean>(false);
    // let {fields: actionParams} = useFieldArray<IReportDescription,'params'>({
    // control, 
    // name: 'params'});

    const { data: enumItems, refetch: refetchEnumerationItems } = useQuery<IEnumerationItem[]>(['EnumerationItems', 'Printing'], () => getEnumerationItemsByEnumerationCodes
        ([Enum_PRODUCTION_UNIT]));


    const [printLanguage, setPrintLanguage] = useState<'' | 'fr-FR' | 'en-US'>(lg.includes('en') ? 'en-US' : 'fr-FR');
    const [reportProductionUnit, setReportProductionUnit] = useState<string>('');
    const [fileName, setFileName] = useState<string>('');
    const [fileToken, setFileToken] = useState<string>('');

    const { isLoading: isReportLoading, isError, isSuccess, error, mutate: mutateEntity } = useMutation<{ fileName: string, fileToken: string, isDirectReport: boolean, hubConnectionId: string }, Error, IReportDescription>(
        generateEntityReport, {
        onSuccess: (data: { fileName: string, fileToken: string, isDirectReport: boolean }) => {

            const { fileName, fileToken, isDirectReport } = data;
            if (isDirectReport) {

                setUrlPdf(`${globalConfig.get().apiUrl}/Download/${fileToken}/${fileName}`);

                setFileName(fileName);
                setFileToken(fileToken);

                setOpenPdf(true);
            }
            else
                enqueueSnackbar(t('_Operation_done'), {
                    variant: 'success',
                    anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
                });

        },
        onError: (err: Error) => {
            enqueueSnackbar(error?.message, {
                variant: 'error',
                anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
            });
        }
    });

    const handleEntityReportClick = async (reportSelected: IReportDescription) => {

        const { name: reportName, label: reportLabel, isDirectReport, code, productionUnitCode, groupId, } = reportSelected;
        setReportProductionUnit(reportSelected.productionUnitCode ?? '')

        if (isFalsy(reportName)) {
            enqueueSnackbar(t('Invalid report'), {
                variant: 'error',
                anchorOrigin: { horizontal: 'left', vertical: 'bottom' }, autoHideDuration: 2000
            });
            return;
        }

        mutateEntity({
            ...reportSelected, language: String(printLanguage),
            entityName, entityId, functionEntityParameters, hubConnectionId,
        });
    }

    const handleDirectPrintClick = async (event: any) => {
        // 👇 call backend print
        try {
            const res = await directPrint(fileToken, fileName, storeId, reportProductionUnit);
            if (res.succeeded) {
                enqueueSnackbar(t('_Operation_done'), {
                    variant: 'success',
                    anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
                });
                setOpenPdf(false);
            } else {
                enqueueSnackbar(res.messages.join('\n'), {
                    variant: 'error',
                    anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
                });
            }
        } catch (err) {
            console.log(err);
        } finally {

        }

    }

    const [urlPdf, setUrlPdf] = useState<string>('');
    const [openPdf, setOpenPdf] = useState<boolean>(false);

    const [storeId, setStoreId] = useState<number>(userStores.find(s => s.isShowroom)?.storeId ?? 0);

    useEffect(() => {

        async function loadEntityReports() {

            const entity = currentUserSession.roleEntities.find(e => e.entityName === entityName);

            if (!entity || entity == null || !entity.printAllowed) {
                setEntityReports([]);
                return;
            }

            const reports = await getReportsByEntityForUser(entityName, entityId);

            setEntityReports(reports.filter(a => true));
        }


        loadEntityReports();
    }, [entityName, entityId, functionEntityParameters, isPrintDrawerOpen]);

    return (
        <Drawer open={isPrintDrawerOpen} anchor="right" variant="temporary"
            PaperProps={{
                sx: {
                    height: 'calc(100vh - 48px)',
                    width: 380,
                    borderTopLeftRadius: 16,
                    borderBottomLeftRadius: 16,
                    boxShadow: '0px 10px 30px rgba(0,0,0,0.18)',
                    top: 48
                }
            }} onClose={() => setIsActionDrawerOpen(false)}>
            {/* <ClickAwayListener onClickAway={() => setIsActionDrawerOpen(false)}> */}

            <Box display="flex" flexDirection="column" height="100%">
                {/* HEADER */}
                <Box sx={{
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0, // 🔴 DO NOT SHRINK
                }}>
                    <Box display="flex" gap={1} alignItems="center">
                        <BoltOutlinedIcon sx={{ fontSize: 22 }} />
                        <Typography fontWeight={700}>{t('Reports')}</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setIsActionDrawerOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Divider />
                <Box sx={{
                    flex: 1,                 // 🔴 TAKES REMAINING SPACE
                    overflowY: 'auto',       // 🔴 ONLY THIS SCROLLS
                    bgcolor: 'grey.25',
                }}>
                    <BasicButtonList<IReportDescription> items={entityReports} fallbackIcon={BoltIcon}
                        onItemClick={handleEntityReportClick}
                        valueKey={'name'} displayKey={'label'} title={t('Select the language and generate the report')}
                        stateSelectedLanguage={[printLanguage, setPrintLanguage]} />

                    {isReportLoading && <Slide direction="down" in={isReportLoading} >
                        <Box sx={{
                            position: "absolute",
                            top: '40%',
                            left: '10%',
                            width: "80%",
                            height: "20%",
                            background: Colors.primary,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 99,
                            opacity: 0.70,
                        }}>

                            <Grid
                                color='#00BFFF'
                                height={'100%'}
                            //width={100}
                            //timeout={3000} //3 secs
                            />
                        </Box>
                    </Slide>
                    }
                </Box>


                {/* </ClickAwayListener> */}
                {openPdf && <FormDialog
                    open={openPdf} okText='' cancelText=''
                    maxWidth='md' title={t('Printing')}
                    onCancel={() => { setOpenPdf(false); }}
                    onClose={() => { setOpenPdf(false); }}
                    onOk={() => { setOpenPdf(false); }}
                >
                    {/* ===== PDF PREVIEW ===== */}
                    <Box
                        sx={{
                            height: '65vh',
                            borderRadius: 1,
                            overflow: 'hidden',
                            mb: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <iframe
                            src={urlPdf}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                            title="pdf-preview"
                        />
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* ===== PRINT TARGET ===== */}
                    <Stack spacing={2}>


                        {/* <Typography variant="body2" color="text.secondary">
                        {t('The document will be printed directly on the default printer of the selected production unit')}
                    </Typography> */}

                        {/* ===== STORE SELECT ===== */}
                        <TextField
                            select
                            size="small"
                            label={t('Print destination')}
                            value={storeId}
                            onChange={(e) => {
                                setStoreId(+e.target.value);
                            }}
                            sx={{ maxWidth: 320 }}
                        // helperText={<Typography variant="subtitle1" fontWeight={600}>
                        //     {t('Print destination')}
                        // </Typography>}
                        >
                            {userStores
                                //.filter(s => s.isShowroom)
                                .map(s => (
                                    <MenuItem key={s.storeId} value={s.storeId}>
                                        {s.storeName}
                                    </MenuItem>
                                ))}
                        </TextField>

                        {/* ===== PRODUCTION UNIT TOGGLE ===== */}
                        <ToggleButtonGroup
                            exclusive
                            fullWidth
                            value={reportProductionUnit}
                            onChange={(_, value) => {
                                if (value !== null) setReportProductionUnit(value);
                            }}
                        >
                            {(enumItems || [])
                                .filter(e => e.enumerationCode === Enum_PRODUCTION_UNIT)
                                .map(u => (
                                    <ToggleButton
                                        key={u.code}
                                        value={u.code}
                                        sx={{
                                            py: 1.5,
                                            fontWeight: 500,
                                        }}
                                    >
                                        <PrintIcon sx={{ mr: 1 }} />
                                        {u.name}
                                    </ToggleButton>
                                ))}
                        </ToggleButtonGroup>

                        {/* ===== PRINT BUTTON ===== */}
                        <Stack direction="row" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<PrintIcon />}
                                disabled={!reportProductionUnit}
                                onClick={handleDirectPrintClick}
                            >
                                {t('Print')}
                            </Button>
                        </Stack>
                    </Stack>
                </FormDialog>
                }
            </Box>
        </Drawer>

    )
}
